/**
 * TenantLicenseTab — Plan detayı + "+X saat uzat" + plan değişimi.
 *
 * Backend endpoint'leri:
 *   GET  /admin/tenants/{id}            — detay payload (lic burada)
 *   POST /admin/tenants/{id}/license/extend     body: { hours }
 *   POST /admin/tenants/{id}/license/change-plan body: { plan }
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { Card, CardHeader, Eyebrow } from "@/components/design-system";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel,
    AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2, Clock, Zap, CheckCircle2 } from "lucide-react";
import { PLAN_META, fmtDate, readError } from "../_tenant-helpers";
import PlanPriceBlock from "../PlanPriceBlock";

export default function TenantLicenseTab({ data, onRefresh }) {
    const lic = data.license || {};
    const traffic = data.traffic || {};
    const [plans, setPlans] = useState([]);
    const [extendHours, setExtendHours] = useState(24);
    const [extendBusy, setExtendBusy] = useState(false);
    const [changeBusy, setChangeBusy] = useState("");
    const [confirmPlan, setConfirmPlan] = useState(null);

    useEffect(() => {
        adminApi.get("/admin/plans")
            .then(({ data }) => setPlans(data?.plans || []))
            .catch(() => setPlans([]));
    }, []);

    const currentPlanKey =
        lic.status === "trial" ? "trial" :
        lic.billing_cycle || "monthly";

    const handleExtend = async () => {
        if (!extendHours || extendHours < 1) return;
        setExtendBusy(true);
        try {
            await adminApi.post(`/admin/tenants/${data.id}/license/extend`, {
                hours: Number(extendHours),
            });
            toast.success(`${extendHours} saat uzatıldı`);
            onRefresh?.();
        } catch (err) {
            toast.error(readError(err));
        } finally {
            setExtendBusy(false);
        }
    };

    const requestChangePlan = (planKey) => {
        if (planKey === currentPlanKey) return;
        setConfirmPlan(planKey);
    };

    const confirmChangePlan = async () => {
        const planKey = confirmPlan;
        if (!planKey) return;
        setChangeBusy(planKey);
        try {
            await adminApi.post(`/admin/tenants/${data.id}/license/change-plan`, { plan: planKey });
            toast.success(`Plan güncellendi: ${PLAN_META[planKey]?.label}`);
            onRefresh?.();
        } catch (err) {
            toast.error(readError(err));
        } finally {
            setChangeBusy("");
            setConfirmPlan(null);
        }
    };

    return (
        <div className="space-y-5" data-testid="license-tab">
            {/* Özet */}
            <Card>
                <CardHeader title="Mevcut Lisans" desc="Hesabın şu an aktif paketi ve sayaçları." />
                <div className="p-5 grid grid-cols-1 md:grid-cols-4 gap-5">
                    <KV label="Mevcut Paket" value={PLAN_META[currentPlanKey]?.label || currentPlanKey} testId="license-current" />
                    <KV label="Durum" value={statusLabel(lic.status)} tone={lic.is_expired ? "danger" : null} testId="license-status" />
                    <KV
                        label={lic.status === "trial" ? "Deneme Bitişi" : "Yenileme Tarihi"}
                        value={fmtDate(lic.status === "trial" ? lic.trial_ends_at : lic.next_billing_at)}
                        hint={lic.is_expired ? "Süresi bitti" :
                              lic.days_remaining != null ? `${lic.days_remaining} gün kaldı` : null}
                        tone={lic.is_expired ? "danger" : (lic.days_remaining != null && lic.days_remaining <= 3) ? "warn" : null}
                        testId="license-end"
                    />
                    <KV label="Aktivasyon" value={lic.activated_at ? fmtDate(lic.activated_at) : "—"} testId="license-activated" />
                </div>

                <div className="px-5 pb-5">
                    <div className="flex items-center justify-between mb-1.5">
                        <Eyebrow>Bu Ay Trafik</Eyebrow>
                        <span className="text-[11px] text-muted-foreground">
                            {traffic.usage_this_month ?? 0} / {traffic.quota_this_month ?? 0}
                        </span>
                    </div>
                    <Progress
                        value={Math.min(100, traffic.usage_pct || 0)}
                        className={`h-1.5 ${(traffic.usage_pct || 0) >= 100 ? "[&>div]:bg-rose-500" : (traffic.usage_pct || 0) >= 80 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"}`}
                        data-testid="license-usage-bar"
                    />
                </div>
            </Card>

            {/* +X saat uzat */}
            <Card>
                <CardHeader
                    title="Hızlı Uzatma"
                    desc="Deneme için trial_days'e, aktif plan için next_billing_at tarihine saat eklenir."
                />
                <div className="p-5">
                    <div className="flex flex-wrap items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        {[6, 12, 24, 72, 168].map((h) => (
                            <Button
                                key={h}
                                variant="outline"
                                size="sm"
                                onClick={() => setExtendHours(h)}
                                className={`h-8 text-[11px] ${extendHours === h ? "bg-foreground text-background hover:bg-foreground/90" : ""}`}
                                data-testid={`license-extend-preset-${h}`}
                            >
                                +{h >= 24 ? `${h / 24} gün` : `${h} saat`}
                            </Button>
                        ))}
                        <div className="flex items-center gap-1.5 ml-2">
                            <Input
                                type="number"
                                min={1}
                                value={extendHours}
                                onChange={(e) => setExtendHours(Number(e.target.value || 0))}
                                className="h-8 w-20 text-xs"
                                data-testid="license-extend-input"
                            />
                            <span className="text-[11px] text-muted-foreground">saat</span>
                        </div>
                        <Button
                            onClick={handleExtend}
                            disabled={!extendHours || extendBusy}
                            className="h-8 text-[11px] bg-foreground text-background hover:bg-foreground/90 ml-auto"
                            data-testid="license-extend-apply"
                        >
                            {extendBusy ? (
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                                <Zap className="h-3 w-3 mr-1" strokeWidth={2} />
                            )}
                            Uzat
                        </Button>
                    </div>
                </div>
            </Card>

            {/* Plan değişimi */}
            <Card>
                <CardHeader
                    title="Paket Değiştir"
                    desc="Yeni paket seçildiğinde başlangıç tarihi bugünden hesaplanır. Mevcut kalan süre sıfırlanır."
                />
                <div className="p-5">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                        {plans.map((p) => (
                            <PlanOption
                                key={p.key}
                                plan={p}
                                current={p.key === currentPlanKey}
                                busy={changeBusy === p.key}
                                onPick={() => requestChangePlan(p.key)}
                            />
                        ))}
                    </div>
                </div>
            </Card>

            {/* Plan değişimi onay dialog'u */}
            <AlertDialog open={Boolean(confirmPlan)} onOpenChange={(o) => !o && setConfirmPlan(null)}>
                <AlertDialogContent data-testid="license-change-plan-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Plan Değişimini Onayla</AlertDialogTitle>
                        <AlertDialogDescription>
                            Plan <b>{PLAN_META[confirmPlan]?.label}</b> olarak güncellenecek.
                            Başlangıç bugünden hesaplanır; mevcut kalan süre sıfırlanır.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="license-change-plan-cancel">Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmChangePlan}
                            disabled={Boolean(changeBusy)}
                            data-testid="license-change-plan-apply"
                        >
                            {changeBusy ? <Loader2 className="h-3 w-3 mr-1 animate-spin inline" /> : null}
                            Onayla
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

function statusLabel(s) {
    if (s === "trial") return "Deneme";
    if (s === "active") return "Aktif";
    if (s === "expired") return "Süresi Bitti";
    return s || "—";
}

function KV({ label, value, tone, hint, testId }) {
    const valueCls =
        tone === "danger" ? "text-rose-600" :
        tone === "warn"   ? "text-amber-600" :
                            "text-foreground";
    return (
        <div data-testid={testId}>
            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                {label}
            </div>
            <div className={`text-[14px] font-semibold mt-0.5 ${valueCls}`}>{value || "—"}</div>
            {hint && <div className={`text-[11px] mt-0.5 ${tone === "danger" ? "text-rose-500" : tone === "warn" ? "text-amber-600" : "text-muted-foreground"}`}>{hint}</div>}
        </div>
    );
}

function PlanOption({ plan, current, busy, onPick }) {
    const meta = PLAN_META[plan.key] || PLAN_META.trial;
    const totalDays = plan.days + (plan.bonus_days || 0);
    return (
        <button
            type="button"
            onClick={onPick}
            disabled={current || busy}
            data-testid={`license-plan-option-${plan.key}`}
            className={[
                "text-left rounded-lg border p-3 transition-all relative",
                current
                    ? "border-foreground bg-foreground/[0.04] cursor-default"
                    : "border-border bg-background hover:border-foreground/40",
                busy ? "opacity-60" : "",
            ].join(" ")}
        >
            <div className="flex items-center justify-between mb-1">
                <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${meta.tone}`}>
                    <span className={`h-1 w-1 rounded-full ${meta.dot}`} />
                    {meta.label}
                </span>
                {current && (
                    <span className="text-[10px] font-semibold text-emerald-600 inline-flex items-center gap-0.5">
                        <CheckCircle2 className="h-3 w-3" strokeWidth={2} /> aktif
                    </span>
                )}
                {busy && <Loader2 className="h-3 w-3 animate-spin" />}
            </div>
            <PlanPriceBlock plan={plan} testId={`license-plan-option-${plan.key}-price`} />
            <div className="text-[11px] text-muted-foreground leading-tight mt-1.5">
                {plan.days} gün{plan.bonus_days ? ` + ${plan.bonus_days} bonus` : ""}
            </div>
            <div className="text-[10px] text-muted-foreground/80 mt-0.5">
                Toplam {totalDays} gün
            </div>
        </button>
    );
}
